// mission-store-read-only.ts
//
// Bug H (P14 real-release-flow dogfood): `avatar autopilot status` threw
// JournalLeaseError whenever a `run` process held the writer lease —
// status-renderer.ts's readStatusSnapshot() called MissionStore.open(), the
// SAME EventJournal.open() lease path the writer uses (event-journal.ts:
// "1. `locks/journal.lock` (O_EXCL, chứa pid) — chặn TIẾN TRÌNH thứ hai.").
// A read-only status view has no business taking that lease at all.
//
// Fix: assemble the SAME {state, status} MissionStore.open() would produce,
// using ONLY the primitives that are already lock-free — scanJournal (plain
// fs.readFile, no O_EXCL) + readProjection + replayRecords — mirroring the
// pattern mission-cost-journal-reader.ts already uses for the identical
// reason (computeMissionCost must also read while `run` holds the lease).
// Mirrors MissionStore.open()'s READY/RECOVERING classification exactly (same
// two RECOVERING triggers: corrupt journal, projection ahead of journal) so
// status shows the same picture a writer would see — just without writing.

import { join } from "node:path";
import { scanJournal } from "./journal-scan-report.js";
import { readProjection, replayRecords } from "./mission-projection-io.js";
import { emptyMissionState, type MissionState } from "./mission-state-reducer.js";

export type MissionStoreReadOnlyStatus = "READY" | "RECOVERING";

/**
 * Read-only counterpart of MissionStore — same `.state`/`.status` surface
 * status-renderer.ts needs, `.close()` is a no-op (nothing was opened).
 * NEVER call `.commit()`-shaped mutations through this — it has none.
 */
export interface MissionStoreReadOnly {
  readonly controlRoot: string;
  readonly state: MissionState;
  readonly status: MissionStoreReadOnlyStatus;
  readonly recoveringReason: string | undefined;
  close(): Promise<void>;
}

const JOURNAL_FILE = "journal.jsonl";
const PROJECTION_FILE = "mission.json";

/**
 * Lock-free equivalent of `MissionStore.open()`. Never calls
 * `EventJournal.open()` — safe to call while another process holds the
 * writer lease (that is the whole point: `status` while `run` is active).
 * Missing controlRoot/journal ⇒ empty READY state (matches scanJournal's own
 * missing-file behavior: mission just hasn't produced any events yet, not an
 * error).
 */
export async function openMissionStoreReadOnly(controlRoot: string): Promise<MissionStoreReadOnly> {
  const journalPath = join(controlRoot, JOURNAL_FILE);
  const scan = await scanJournal(journalPath);
  const close = async (): Promise<void> => {
    // Nothing was opened — no fd, no lease. Kept for interface parity with
    // MissionStore so call sites can `finally { await store.close() }`
    // uniformly regardless of which one they hold.
  };

  if (scan.status === "corrupt") {
    const reason =
      `Journal hỏng tại offset ${scan.problem?.offset} (${scan.problem?.kind}` +
      `${scan.problem?.seq !== undefined ? `, seq ${scan.problem.seq}` : ""}). ` +
      "Record đủ newline mà sai ⇒ KHÔNG skip; cần người xem xét.";
    return {
      controlRoot,
      state: emptyMissionState(),
      status: "RECOVERING",
      recoveringReason: reason,
      close,
    };
  }

  // partial_tail: status is a READER, not the recovery authority — show the
  // clean prefix (scan.records already excludes the unterminated tail bytes,
  // journal-scan-report.ts's own contract) rather than blocking on a repair
  // only a writer (`run`) is allowed to perform.
  const projection = await readProjection(join(controlRoot, PROJECTION_FILE));
  if (projection && projection.last_event_seq > scan.lastSeq) {
    const reason =
      `Projection đi TRƯỚC journal (projection seq ${projection.last_event_seq} > ` +
      `journal seq ${scan.lastSeq}) — bằng chứng mất dữ liệu, phải điều tra.`;
    return {
      controlRoot,
      state: projection,
      status: "RECOVERING",
      recoveringReason: reason,
      close,
    };
  }

  const base = projection ?? emptyMissionState();
  const state = replayRecords(base, scan.records, base.last_event_seq + 1);
  return { controlRoot, state, status: "READY", recoveringReason: undefined, close };
}
